/**
 * URL Helper Functions for SEO-Friendly URLs
 * Generates slugs and builds semantic URLs for jobs and companies
 */

/**
 * Generate a URL-friendly slug from text
 * @param {string} text - Text to convert to slug
 * @param {number} maxLength - Maximum length of slug (default: 50)
 * @returns {string} - URL-friendly slug
 *
 * @example
 * generateSlug("Senior Developer at Google") // "senior-developer-at-google"
 * generateSlug("Full-Stack Engineer (Remote)") // "full-stack-engineer-remote"
 */
export const generateSlug = (text, maxLength = 50) => {
  if (!text || typeof text !== 'string') return '';

  return text
    .toLowerCase() // Convert to lowercase
    .trim() // Remove leading/trailing whitespace
    .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
    .substring(0, maxLength) // Limit length for SEO
    .replace(/-+$/g, ''); // Remove trailing hyphen if substring cut in middle
};

/**
 * Build SEO-friendly job URL with slug
 * @param {string} jobId - Job UUID
 * @param {string} jobTitle - Job title
 * @param {string} companyName - Company name (optional)
 * @returns {string} - Full job URL path
 *
 * @example
 * buildJobUrl("123-uuid", "Senior Developer", "Google")
 * // "/job/123-uuid/senior-developer-at-google"
 */
export const buildJobUrl = (jobId, jobTitle, companyName = '') => {
  if (!jobId) return '/jobs'; // Fallback to jobs list

  // Build descriptive slug
  let slugText = jobTitle || 'job';
  if (companyName) {
    slugText = `${jobTitle} at ${companyName}`;
  }

  const slug = generateSlug(slugText);

  // Return URL with optional slug
  return slug ? `/job/${jobId}/${slug}` : `/job/${jobId}`;
};

/**
 * Build SEO-friendly company URL with slug
 * @param {string} companyId - Company UUID
 * @param {string} companyName - Company name
 * @returns {string} - Full company URL path
 *
 * @example
 * buildCompanyUrl("456-uuid", "Google")
 * // "/company/456-uuid/google"
 */
export const buildCompanyUrl = (companyId, companyName) => {
  if (!companyId) return '/startups'; // Fallback to startups/companies list

  const slug = generateSlug(companyName || 'company');

  // Return URL with optional slug
  return slug ? `/company/${companyId}/${slug}` : `/company/${companyId}`;
};

/**
 * Extract ID from URL pathname (works with both old and new URL formats)
 * @param {string} pathname - URL pathname
 * @returns {string|null} - Extracted ID or null
 *
 * @example
 * extractIdFromUrl("/job/123-uuid") // "123-uuid"
 * extractIdFromUrl("/job/123-uuid/senior-developer") // "123-uuid"
 */
export const extractIdFromUrl = (pathname) => {
  if (!pathname) return null;

  // Match patterns like /job/:id or /job/:id/:slug
  const jobMatch = pathname.match(/^\/job\/([^/]+)/);
  if (jobMatch) return jobMatch[1];

  // Match patterns like /company/:id or /company/:id/:slug
  const companyMatch = pathname.match(/^\/company\/([^/]+)/);
  if (companyMatch) return companyMatch[1];

  return null;
};

/**
 * Check if URL has a slug (for redirect logic)
 * @param {string} pathname - URL pathname
 * @returns {boolean} - True if URL has slug
 *
 * @example
 * hasSlug("/job/123-uuid/senior-developer") // true
 * hasSlug("/job/123-uuid") // false
 */
export const hasSlug = (pathname) => {
  if (!pathname) return false;

  // Count slashes - URLs with slugs have 3+ slashes
  const parts = pathname.split('/').filter(Boolean);
  return parts.length >= 3;
};
